import {Box, Tabs} from '@yandex-delivery/blocks'
import type {TabItem} from '@yandex-delivery/blocks/tabs/Tabs'
import {type ReactElement, useCallback, useMemo} from 'react'
import {useController, useFormContext} from 'react-hook-form'

import {useFormUserEventHandler} from '../../lib/metrika'
import type {OrderFormState} from '../../types'
import {renderWidget} from '..'
import {Badge} from '../Badge'
import type {SegmentWidgetProps as Props} from '../types'
import {box, segment} from './classes'

export const SegmentWidget = ({
  config: {
    action: {form_state_key, metrica_label, options},
  },
}: Props): ReactElement => {
  const {control} = useFormContext<OrderFormState>()
  const {field} = useController({
    name: form_state_key,
    control,
    defaultValue: options.find(option => option.default)?.value,
  })

  const handleReachGoal = useFormUserEventHandler()

  const items: Array<TabItem<string>> = useMemo(
    () =>
      options.map(option => ({
        value: String(option.value),
        label: option[option.value === field.value ? 'selected_text' : 'unselected_text'].title.items.map(
          (item, index) => (
            <Box key={`${form_state_key}-${index}`} className={box} px="s">
              {option.badge && <Badge {...option.badge} />}
              {renderWidget(item)}
            </Box>
          ),
        ),
      })),
    [field.value, form_state_key, options],
  )

  const disabled: Array<string> | undefined = useMemo(
    () => options.filter(option => !option.is_available).map(option => String(option.value)),
    [options],
  )

  const handleChange = useCallback(
    (value: string) => {
      if (value !== field.value) {
        const option = options.find(option => String(option.value) === value)
        // Голза Метрики может лежать либо в самом опшине, либо для всего блока. В приоритете ставим голз опшина
        const metricaLabel = option?.metrica_label || metrica_label

        if (metricaLabel) {
          handleReachGoal(metricaLabel)
        }

        const intValue = parseInt(value)
        field.onChange(Number.isNaN(intValue) ? value : intValue)
      }
    },
    [field, handleReachGoal, metrica_label, options],
  )

  return (
    <Tabs.Segments<string>
      className={segment}
      disabled={disabled}
      items={items}
      onChange={handleChange}
      size="s"
      value={String(field.value)}
    />
  )
}

SegmentWidget.displayName = 'Segment'
